/**
 * The steps of the KBo flow, as offered by the Flow Step Debug "step to debug" picker.
 *
 * This catalog is hardcoded on purpose: the flow lives in the separate OrchestrationEngine
 * repository and the platform has no way to query it. Every field below is transcribed from
 * two files there, which are the only place the step → skill mapping exists:
 *
 *   OrchestrationEngine/Workflows/KboFlowWorkflow.cs   the ordered activities and their labels
 *   KboFlow/Agents/KboFlowAgentFactory.cs              the skills each agent role loads
 *
 * When a step changes there, change it here.
 */

export interface FlowStep {
    /** Stable id used in the URL and as the select value. */
    id: string;
    /** Label as the mockup names the step. */
    label: string;
    /** Elsa activity display name in KboFlowWorkflow, e.g. "4c · Analyze Node". */
    activityName: string;
    /**
     * Kbo.Flow phase names whose presence in a run's `phaseRecords` means the run executed
     * this step. Several steps share one recorded phase (the multi-level analysis phase does
     * not record its internal steps separately), and some phases were renamed across run
     * versions, so this is a list rather than a single name. Empty means "cannot be
     * attributed to a phase" — the picker then offers any completed run.
     */
    phases: string[];
    /**
     * Names of the AcxAgentSkills this step's agents load, matched case-insensitively against
     * the KboFlow-scoped skills read from the core database. Empty means the step runs no
     * agent that loads a skill — most of the flow is plain orchestration, and the four judges
     * in question validation carry their rules in their prompts rather than in a skill.
     */
    skillNames: string[];
}

/** Skills the analysis of a single node loads, across its dimension, conversation, and selection agents. */
const NODE_ANALYSIS_SKILLS = [
    "general-dimension-selection",
    "conversation-analysis",
    "select-relevant-findings",
    "should-continue-analysis",
];

export const FLOW_STEPS: FlowStep[] = [
    {
        id: "context-assembly",
        label: "1. Context Assembly",
        activityName: "1 · Assemble Context",
        phases: ["ContextAssembly"],
        skillNames: [],
    },
    {
        id: "question-generation",
        label: "2. Question Generation",
        activityName: "2 · Generate Questions",
        phases: ["QuestionGeneration"],
        skillNames: [],
    },
    {
        id: "question-validation",
        label: "3. Question Validation",
        activityName: "3 · Validate & Select Questions",
        phases: ["QuestionValidation"],
        skillNames: [],
    },
    {
        id: "seed-root-nodes",
        label: "4a. Seed Root Nodes",
        activityName: "4a · Seed Root Nodes",
        phases: ["MultiLevelAnalysis", "Analysis"],
        skillNames: [],
    },
    {
        id: "load-depth-nodes",
        label: "4b. Load Depth Nodes",
        activityName: "4b · Load Depth Nodes",
        phases: ["MultiLevelAnalysis", "Analysis"],
        skillNames: [],
    },
    {
        // The production workflow also fans this same per-node logic out across a whole depth in
        // parallel ("4c · Analyze Nodes (parallel)"), but that variant is deliberately not offered
        // here: testing a skill edit against one chosen node is the useful unit for this tool, and
        // the parallel activity loads the exact same skills, so a second catalog entry would only
        // add a picker choice without adding anything to compare.
        id: "analyze-one-node",
        label: "4c. Analyze Node",
        activityName: "4c · Analyze Node",
        phases: ["MultiLevelAnalysis", "Analysis"],
        skillNames: NODE_ANALYSIS_SKILLS,
    },
    {
        id: "expand-findings",
        label: "4d. Expand Findings",
        activityName: "4d · Expand Results",
        phases: ["MultiLevelAnalysis", "Analysis"],
        skillNames: [],
    },
    {
        // The report selection agent loads select-relevant-findings; the synthesis agent that
        // follows it loads summarize-analysis and then create-email-report.
        id: "report-assembly",
        label: "5. Report Assembly",
        activityName: "5 · Assemble Reports",
        phases: ["ReportAssembly", "MultiLevelReportAssembly"],
        skillNames: [
            "select-relevant-findings",
            "summarize-analysis",
            "create-email-report",
        ],
    },
    {
        id: "commit-run-serialization",
        label: "6. Commit Run Serialization",
        activityName: "6 · Commit Run",
        phases: ["Commit", "MultiLevelAnalysisCommit"],
        skillNames: [],
    },
];

// Note: a `create-graph-visual` skill also ships with KboFlow, but its only call site
// (MultiLevelAnalysisService.BuildPassVisuals) is currently commented out, so no step loads it.

export const DEFAULT_FLOW_STEP_ID = "context-assembly";

export function findFlowStep(stepId: string | undefined): FlowStep | undefined {
    return FLOW_STEPS.find((step) => step.id === stepId);
}

/** Date windows offered by the run picker. `days: 0` means "any time". */
export const RUN_DATE_WINDOWS: { days: number; label: string }[] = [
    { days: 7, label: "Last 7 days" },
    { days: 30, label: "Last 30 days" },
    { days: 90, label: "Last 90 days" },
    { days: 0, label: "Any time" },
];

/** Input sources offered by the manual-input panel. */
export const MANUAL_INPUT_SOURCES: { id: string; label: string }[] = [
    { id: "pastedJson", label: "Pasted JSON" },
];
